import styles from '../styles/About.module.css';

function About() {
    return (
        <div className={styles.about}>
            <section className={styles.hero}>
                <h1>About Me</h1>
                <p className={styles.subtitle}>
                    Software Engineer | Architect | Open Source Contributor
                </p>
                <div className={styles.socialLinks}>
                    <a
                        href="https://github.com/pnagoorkar"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="GitHub Profile"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                    </a>
                    <a
                        href="https://www.linkedin.com/in/prashantnagoorkar/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn Profile"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                    </a>
                </div>
            </section>

            <section className={styles.content}>
                <div className={styles.section}>
                    <p>
                        I specialize in designing and building systems with richly polymorphic,
                        highly variant entities - as modular monoliths and microservices with
                        minimal coupling, particularly in the manufacturing, electronics and
                        industrial engineering domains. My code is strongly influenced by Clean
                        Architecture, SOLID, and DDD principles. While I gravitate toward
                        object-oriented, type-safe designs, I also appreciate the strengths of
                        functional programming, especially fluent APIs and result types. Having
                        benefited immensely from the blogs and open-source projects of others, this
                        blog is my way of giving back to the community.
                    </p>
                    <p>Some of the projects I am currently working on:</p>
                    <div className={styles.projectsList}>
                        <div className={styles.projectGroup}>
                            <strong>Core</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.DI"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.DI
                                    </a>{' '}
                                    - Modularity framework with configuration-driven module
                                    composition
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Configuration"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Configuration
                                    </a>{' '}
                                    - Type-safe configuration builder with Result pattern and
                                    environment variable expansion
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.DI.Autofac"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.DI.Autofac
                                    </a>{' '}
                                    - Autofac support for Baubit.DI
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>High Performance</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Identity"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Identity
                                    </a>{' '}
                                    - Monotonic GuidV7 generator. Zero allocations, thread-safe, RFC
                                    9562 compliant. Targets netstandard2.0
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Caching"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Caching
                                    </a>{' '}
                                    - Thread-safe ordered cache. O(1) lookups, two-tier storage,
                                    async enumeration
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Mediation"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Mediation
                                    </a>{' '}
                                    - Mediator pattern with cache-backed routing. 3-4x faster than
                                    MediatR
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>Utilities</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Reflection"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Reflection
                                    </a>{' '}
                                    - Enhanced reflection with Result pattern error handling
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Collections"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Collections
                                    </a>{' '}
                                    - Thread-safe collection types (ConcurrentList{'<'}T{'>'})
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Tasks"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Tasks
                                    </a>{' '}
                                    - Task utilities with FluentResults integration
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Networking"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Networking
                                    </a>{' '}
                                    - TCP utilities for testing and inter-process communication
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>Persistence</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Caching.LiteDB"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Caching.LiteDB
                                    </a>{' '}
                                    - LiteDB-backed L2 store implementation for Baubit.Caching.
                                    Useful for session persistence, rewind-replay
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>DI Extensions</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.DI.Extensions"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.DI.Extensions
                                    </a>{' '}
                                    - Extensions for adding modules directly to IServiceCollection
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Caching.DI"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Caching.DI
                                    </a>{' '}
                                    - DI modules for Baubit.Caching
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Caching.LiteDB.DI"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Caching.LiteDB.DI
                                    </a>{' '}
                                    - DI modules for Baubit.Caching.LiteDB
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Mediation.DI"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Mediation.DI
                                    </a>{' '}
                                    - DI modules for Baubit.Mediation
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>Testing</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Testing"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Testing
                                    </a>{' '}
                                    - Unit testing support with scenario-based testing. Currently
                                    WIP
                                </li>
                            </ul>
                        </div>

                        <div className={styles.projectGroup}>
                            <strong>Other Projects</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/Baubit.Traceability"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Baubit.Traceability
                                    </a>{' '}
                                    - Error handling and result tracing extensions for FluentResults
                                </li>
                                <li>
                                    <a
                                        href="https://github.com/pnagoorkar/fluent-results"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        fluent-results
                                    </a>{' '}
                                    - Dependency-free success/failure pipelines for JavaScript &
                                    TypeScript
                                </li>
                            </ul>
                        </div>

                        <hr className={styles.separator} />

                        <div className={styles.projectGroup}>
                            <strong>Other Contributions</strong>
                            <ul>
                                <li>
                                    <a
                                        href="https://github.com/autofac/Autofac.Configuration"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Autofac.Configuration
                                    </a>{' '}
                                    - Module construction improvements
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default About;
